import React, { useEffect, useRef, useState } from "react";
import Cat from "@/game/Cat";
import sfx from "@/game/sfx";
import { useTr } from "@/autoTranslate";

/**
 * Milkoş Fish Catch — cat at bottom, ← / → to move. Catch falling fish.
 * Miss 3 → game over. Time bonus every 10 catches.
 */
const STAGE_W = 640;
const STAGE_H = 340;
const CAT_SIZE = 60;
const CATCH_SPEED = 480;

export default function MilosFishCatch({ onExit }) {
  const tr = useTr();
  const [best, setBest] = useState(() =>
    parseInt(localStorage.getItem("milos_fish_best") || "0", 10),
  );
  const [score, setScore] = useState(0);
  const [misses, setMisses] = useState(0);
  const [state, setState] = useState("idle");
  const [catX, setCatX] = useState(STAGE_W / 2 - CAT_SIZE / 2);
  const catXRef = useRef(STAGE_W / 2 - CAT_SIZE / 2);
  const [fish, setFish] = useState([]);
  const [dir, setDir] = useState(1);
  const rafRef = useRef();
  const lastTsRef = useRef(0);
  const spawnRef = useRef(0);
  const keysRef = useRef({});
  const scoreRef = useRef(0);
  const missesRef = useRef(0);

  const start = () => {
    setScore(0);
    scoreRef.current = 0;
    setMisses(0);
    missesRef.current = 0;
    setFish([]);
    spawnRef.current = 0;
    catXRef.current = STAGE_W / 2 - CAT_SIZE / 2;
    setCatX(catXRef.current);
    setState("playing");
  };

  useEffect(() => {
    const down = (e) => {
      if (e.code === "ArrowLeft" || e.code === "ArrowRight") {
        keysRef.current[e.code] = true;
        e.preventDefault();
      }
      if (e.code === "Escape") onExit && onExit();
      if ((e.code === "Space" || e.code === "Enter") && state !== "playing") {
        start();
        e.preventDefault();
      }
    };
    const up = (e) => (keysRef.current[e.code] = false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [state, onExit]);

  useEffect(() => {
    if (state !== "playing") return;
    lastTsRef.current = 0;
    const loop = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = Math.min(0.05, (ts - lastTsRef.current) / 1000);
      lastTsRef.current = ts;

      // Cat movement
      const keys = keysRef.current;
      let dx = 0;
      if (keys["ArrowLeft"]) dx -= 1;
      if (keys["ArrowRight"]) dx += 1;
      if (dx !== 0) setDir(dx);
      catXRef.current = Math.max(
        0,
        Math.min(STAGE_W - CAT_SIZE, catXRef.current + dx * CATCH_SPEED * dt),
      );
      setCatX(catXRef.current);

      // Spawn
      const level = Math.floor(scoreRef.current / 6);
      const fallSpeed = 160 + level * 30;
      const spawnEvery = Math.max(0.55, 1.1 - level * 0.08);
      spawnRef.current += dt;
      if (spawnRef.current > spawnEvery) {
        spawnRef.current = 0;
        const bonus = Math.random() < 0.12;
        setFish((prev) => [
          ...prev,
          {
            id: `${Date.now()}-${Math.random()}`,
            x: Math.random() * (STAGE_W - 40) + 20,
            y: -30,
            v: fallSpeed + (bonus ? 60 : 0),
            bonus,
          },
        ]);
      }

      // Move fish + collide
      setFish((prev) => {
        const next = [];
        const catBox = {
          x: catXRef.current + 6,
          y: STAGE_H - CAT_SIZE - 8,
          w: CAT_SIZE - 12,
          h: CAT_SIZE - 12,
        };
        for (const f of prev) {
          const ny = f.y + f.v * dt;
          const fb = { x: f.x - 14, y: ny - 12, w: 28, h: 24 };
          if (
            catBox.x < fb.x + fb.w &&
            catBox.x + catBox.w > fb.x &&
            catBox.y < fb.y + fb.h &&
            catBox.y + catBox.h > fb.y
          ) {
            scoreRef.current += f.bonus ? 3 : 1;
            setScore(scoreRef.current);
            sfx.pop();
          } else if (ny > STAGE_H) {
            missesRef.current += 1;
            setMisses(missesRef.current);
          } else {
            next.push({ ...f, y: ny });
          }
        }
        return next;
      });

      if (missesRef.current >= 3) {
        setState("over");
        if (scoreRef.current > best) {
          setBest(scoreRef.current);
          localStorage.setItem("milos_fish_best", String(scoreRef.current));
        }
        return;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [state, best]);

  return (
    <div className="w-full" data-testid="minigame-milos-fish" data-minigame-active="true">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
            {tr("Kedi oyunu")}
          </div>
          <h3 className="mt-1 display-serif italic text-4xl md:text-5xl">
            {tr("Balık Yakala")}
          </h3>
        </div>
        <div className="text-right">
          <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
            {tr("Rekor")} · {best}
          </div>
          <div className="display-serif italic text-3xl">{score}</div>
          <div className="mt-1 flex gap-1 justify-end">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="text-xl"
                style={{
                  color:
                    i < 3 - misses
                      ? "var(--pink-hot)"
                      : "rgba(74,42,58,0.2)",
                }}
              >
                ♥
              </span>
            ))}
          </div>
        </div>
      </div>

      <div
        className="mt-6 relative mx-auto rounded-md border-2 border-[color:var(--ink)] overflow-hidden"
        style={{
          width: STAGE_W,
          maxWidth: "100%",
          height: STAGE_H,
          background:
            "linear-gradient(180deg, var(--lavender) 0%, var(--mint) 100%)",
        }}
      >
        {/* Water surface line */}
        <div
          className="absolute left-0 right-0 border-t-2 border-[color:var(--ink)]"
          style={{ top: STAGE_H - 40, background: "var(--peach)", height: 40 }}
        />
        {/* Cat */}
        <div
          className="absolute"
          style={{
            left: catX,
            bottom: 8,
            width: CAT_SIZE,
            height: CAT_SIZE,
          }}
        >
          <Cat dir={dir} sitting={false} size={CAT_SIZE} />
        </div>
        {/* Fish */}
        {fish.map((f) => (
          <div
            key={f.id}
            className="absolute rounded-full border-2 border-[color:var(--ink)] flex items-center justify-center text-lg"
            style={{
              left: f.x - 14,
              top: f.y - 12,
              width: 28,
              height: 24,
              background: f.bonus ? "var(--gold)" : "var(--pink)",
            }}
          >
            {f.bonus ? "★" : "🐟"}
          </div>
        ))}
        {state !== "playing" && (
          <div className="absolute inset-0 flex items-center justify-center flex-col bg-[rgba(255,241,230,0.9)]">
            <div className="display-serif italic text-4xl">
              {state === "idle" ? tr("Balıkları yakala!") : `${tr("Güzel")} — ${score}`}
            </div>
            <div className="mt-2 font-mono-label text-[10px] uppercase tracking-widest opacity-70">
              {tr("← → tuşlarıyla hareket et · ★ = 3 puan · 3 balık kaçırırsan biter")}
            </div>
            <div className="mt-4 flex gap-2">
              <button className="btn-pill warm" onClick={start} data-testid="fish-start">
                {state === "idle" ? tr("Başla") : tr("Tekrar")} →
              </button>
              <button className="btn-pill ghost" onClick={onExit} data-testid="fish-exit">
                {tr("Çık")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
